import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
    static targets = ['link', 'submenu'];

    connect() {
        // Close menus when clicking outside
        document.addEventListener('click', this.handleOutsideClick.bind(this));
    }

    disconnect() {
        document.removeEventListener('click', this.handleOutsideClick.bind(this));
    }

    // Toggle submenu on click
    toggle(event) {
        const link = event.currentTarget;
        const isExpanded = link.getAttribute('aria-expanded') === 'true';

        // If submenu is open, allow normal navigation
        // If submenu is closed, prevent default and open it
        if (!isExpanded) {
            event.preventDefault();
            event.stopPropagation();

            // Close all sibling menus (same level)
            this.closeSiblings(link);

            // Open current menu
            this.open(link);
        }
        // If already expanded, let the link work normally (navigate to URL)
    }

    // Handle keyboard navigation
    handleKeydown(event) {
        const link = event.currentTarget;
        const hasSubmenu = link.getAttribute('aria-haspopup') === 'true';
        const isExpanded = link.getAttribute('aria-expanded') === 'true';
        const isInSubmenu = link.closest('.sub-menu') !== null;

        // Get current context
        const currentSubmenu = link.closest('.sub-menu');
        const submenu = this.getSubmenu(link);
        const submenuLinks = submenu ? submenu.querySelectorAll(':scope > li > a') : [];

        // Get navigation context for submenu items
        let siblingLinks = [];
        let currentIndex = -1;
        let parentLink = null;

        if (currentSubmenu) {
            siblingLinks = Array.from(currentSubmenu.querySelectorAll(':scope > li > a'));
            currentIndex = siblingLinks.indexOf(link);
            parentLink = this.getParentLink(currentSubmenu);
        }

        switch (event.key) {
            case 'Enter':
                if (hasSubmenu && !isExpanded) {
                    event.preventDefault();
                    this.open(link);
                    setTimeout(() => submenuLinks[0]?.focus(), 100);
                }
                // If expanded or no submenu, let Enter work normally (follow link)
                break;

            case ' ':
                if (hasSubmenu) {
                    event.preventDefault();
                    if (!isExpanded) {
                        this.open(link);
                        setTimeout(() => submenuLinks[0]?.focus(), 100);
                    } else if (isExpanded) {
                        this.close(link);
                    }
                }
                break;

            case 'Escape':
                event.preventDefault();
                if (isInSubmenu) {
                    // Close current level and all parent levels
                    this.closeCurrentAndParents(currentSubmenu);
                    // Focus the top-level parent
                    const topParent = this.getTopLevelParent(currentSubmenu);
                    topParent?.focus();
                } else if (hasSubmenu && isExpanded) {
                    // Close top-level submenu
                    this.close(link);
                    link.focus();
                }
                break;

            case 'ArrowDown':
                event.preventDefault();
                if (isInSubmenu) {
                    // Navigate to next sibling in submenu
                    const nextIndex = (currentIndex + 1) % siblingLinks.length;
                    siblingLinks[nextIndex]?.focus();
                } else if (hasSubmenu) {
                    // Open submenu and focus first item
                    if (!isExpanded) {
                        this.open(link);
                    }
                    setTimeout(() => submenuLinks[0]?.focus(), 100);
                }
                break;

            case 'ArrowUp':
                if (isInSubmenu) {
                    event.preventDefault();
                    if (currentIndex === 0) {
                        // Go back to parent
                        parentLink?.focus();
                    } else {
                        // Navigate to previous sibling
                        siblingLinks[currentIndex - 1]?.focus();
                    }
                }
                break;

            case 'ArrowRight':
                if (hasSubmenu) {
                    event.preventDefault();
                    if (!isExpanded) {
                        this.open(link);
                    }
                    setTimeout(() => submenuLinks[0]?.focus(), 100);
                }
                break;

            case 'ArrowLeft':
                if (isInSubmenu) {
                    event.preventDefault();
                    if (parentLink) {
                        this.close(parentLink);
                        parentLink.focus();
                    }
                }
                break;
        }
    }

    // Mouse enter handler
    mouseEnter(event) {
        const link = event.currentTarget;
        const parentLi = link.closest('.has-submenu');

        // Only handle mouseenter on the parent link itself
        if (parentLi) {
            // Close sibling menus
            this.closeSiblings(link);

            // Open current menu
            this.open(link);

            // Add mouseleave to the parent li
            parentLi.addEventListener('mouseleave', () => {
                this.close(link);
            }, { once: true });
        }
    }

    // Get submenu for a link (next sibling element)
    getSubmenu(link) {
        const parentLi = link.parentElement;
        return parentLi ? parentLi.querySelector(':scope > .sub-menu') : null;
    }

    // Get the parent link of a submenu
    getParentLink(submenu) {
        const parentLi = submenu.parentElement;
        return parentLi ? parentLi.querySelector(':scope > a[aria-haspopup="true"]') : null;
    }

    // Get the top-level parent link
    getTopLevelParent(submenu) {
        let current = submenu;
        let parentLink = null;

        while (current) {
            parentLink = this.getParentLink(current);
            const parentSubmenu = parentLink ? parentLink.closest('.sub-menu') : null;
            if (!parentSubmenu) {
                return parentLink;
            }
            current = parentSubmenu;
        }

        return parentLink;
    }

    // Close current submenu and all parent submenus
    closeCurrentAndParents(submenu) {
        let current = submenu;

        while (current) {
            const parentLink = this.getParentLink(current);
            if (parentLink) {
                this.close(parentLink);
                current = parentLink.closest('.sub-menu');
            } else {
                break;
            }
        }
    }

    // Close sibling menus (at the same level)
    closeSiblings(link) {
        const parentLi = link.parentElement;
        const parentUl = parentLi.parentElement;
        const siblingLinks = parentUl.querySelectorAll(':scope > li > a[aria-haspopup="true"]');

        siblingLinks.forEach(siblingLink => {
            if (siblingLink !== link) {
                this.close(siblingLink);
            }
        });
    }

    // Open menu
    open(link) {
        const submenu = this.getSubmenu(link);

        if (submenu) {
            link.setAttribute('aria-expanded', 'true');
            submenu.setAttribute('aria-hidden', 'false');
        }
    }

    // Close menu
    close(link) {
        const submenu = this.getSubmenu(link);

        if (submenu) {
            link.setAttribute('aria-expanded', 'false');
            submenu.setAttribute('aria-hidden', 'true');

            // Also close all child submenus
            const childLinks = submenu.querySelectorAll('a[aria-haspopup="true"]');
            childLinks.forEach(childLink => this.close(childLink));
        }
    }

    // Close all menus
    closeAll() {
        const allLinks = this.element.querySelectorAll('a[aria-haspopup="true"]');
        allLinks.forEach(link => this.close(link));
    }

    // Handle clicks outside navigation
    handleOutsideClick(event) {
        if (!this.element.contains(event.target)) {
            this.closeAll();
        }
    }
}
